import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  HeartPulse,
  Info,
  ListOrdered,
  ShieldAlert,
} from "lucide-react";
import { buildHypertensionFormStyles } from "./hypertension/styleHelpers";
import {
  HYPERTENSION_STAGES,
  HYPERTENSION_SERVICE_NAME,
  getStage,
} from "@/lib/hypertensionCaseFinding";
import { links } from "@/lib/linksRegistry";
import { ClinicalReferencesCollapsible } from "./ClinicalReferencesCollapsible";

export interface NHSHypertensionInitialFormData {
  patientConsent: "yes" | "no" | "";
  consultationDate: string;
  consultationTime: string;
  consultationMethod: "face-to-face" | "telephone" | "video" | "";

  // Eligibility
  ageEligible: "yes" | "no" | "";
  alreadyDiagnosed: "yes" | "no" | "";
  pregnant: "yes" | "no" | "n-a" | "";
  methodOfEntry:
    | "pharmacy-identified"
    | "self-referral"
    | "gp-practice"
    | "covid-vaccination-centre"
    | "optician"
    | "dentist"
    | "";
  referrerCaseReference: string;
  referredFor: "clinic-bp-check" | "abpm" | "";

  // Clinic blood pressure (NICE NG136 — three readings)
  bp1Systolic: string;
  bp1Diastolic: string;
  bp1Arm: "left" | "right" | "";
  bp2Systolic: string;
  bp2Diastolic: string;
  bp2Arm: "left" | "right" | "";
  bp3Systolic: string;
  bp3Diastolic: string;
  bp3Arm: "left" | "right" | "";
  pulseRate: string;
  irregularPulse: boolean;

  // ABPM consent
  abpmConsent: "yes" | "no" | "";
  abpmAvailableNow: "yes" | "no" | "";

  // Low BP symptoms (only collected when avg BP is in the low band)
  lowBpSymptomCategory: "fainting" | "dizziness-nausea-fatigue" | "none" | "";
  lowBpFaintingSymptoms: Array<
    "regular-fainting" | "falls" | "feel-may-faint-daily"
  >;
  lowBpDnfSymptoms: Array<"dizziness" | "nausea" | "fatigue">;

  // Healthy living advice given to the patient
  healthyLivingAdvice: Array<
    | "diet-nutrition"
    | "sodium-salt"
    | "caffeine"
    | "alcohol"
    | "smoking"
    | "physical-activity"
    | "referral-local-healthy-living"
  >;
  healthyLivingAdviceNotes: string;

  // Decision
  outcome: "offer-abpm" | "no-action-normal" | "refer-urgent" | "refer-gp" | "";
  outcomeNotes: string;

  // Treatment plan / next stage
  scheduleAbpmFitting: boolean;
  abpmFittingDate: string;
  abpmFollowUpDate: string;
  internalNotes: string;
}

interface NHSHypertensionInitialFormProps {
  isDarkMode?: boolean;
  onSave?: (data: NHSHypertensionInitialFormData) => void;
  onComplete?: (data: NHSHypertensionInitialFormData) => void;
  initialData?: Partial<NHSHypertensionInitialFormData>;
  nhsHypertensionInitialFormLoading?: boolean;
  consultationStatus?: number;
}

const defaultData: NHSHypertensionInitialFormData = {
  patientConsent: "",
  consultationDate: new Date().toISOString().split("T")[0],
  consultationTime: new Date().toTimeString().slice(0, 5),
  consultationMethod: "face-to-face",
  ageEligible: "",
  alreadyDiagnosed: "",
  pregnant: "",
  methodOfEntry: "",
  referrerCaseReference: "",
  referredFor: "",
  bp1Systolic: "",
  bp1Diastolic: "",
  bp1Arm: "",
  bp2Systolic: "",
  bp2Diastolic: "",
  bp2Arm: "",
  bp3Systolic: "",
  bp3Diastolic: "",
  bp3Arm: "",
  pulseRate: "",
  irregularPulse: false,
  abpmConsent: "",
  abpmAvailableNow: "",
  lowBpSymptomCategory: "",
  lowBpFaintingSymptoms: [],
  lowBpDnfSymptoms: [],
  healthyLivingAdvice: [],
  healthyLivingAdviceNotes: "",
  outcome: "",
  outcomeNotes: "",
  scheduleAbpmFitting: true,
  abpmFittingDate: "",
  abpmFollowUpDate: "",
  internalNotes: "",
};

const toNum = (v: string) => (v === "" ? null : Number(v));

const calcAvgBp = (data: NHSHypertensionInitialFormData) => {
  const sys = [data.bp2Systolic, data.bp3Systolic]
    .map(toNum)
    .filter((n): n is number => n !== null && !Number.isNaN(n));
  const dia = [data.bp2Diastolic, data.bp3Diastolic]
    .map(toNum)
    .filter((n): n is number => n !== null && !Number.isNaN(n));
  if (sys.length === 0 || dia.length === 0) return null;
  return {
    systolic: Math.round(sys.reduce((a, b) => a + b, 0) / sys.length),
    diastolic: Math.round(dia.reduce((a, b) => a + b, 0) / dia.length),
  };
};

// Reference chart showing the four NHS blood pressure categories.
// Defaults to a compact horizontal layout (one row of four bands) so it
// doesn't dominate the section, and can be expanded to a more detailed
// vertical layout with the band ranges and labels spelled out.
function BpReferenceChart({
  isDarkMode,
  avg,
}: {
  isDarkMode: boolean;
  avg: { systolic: number; diastolic: number } | null;
}) {
  const [expanded, setExpanded] = useState(false);

  const bands = [
    {
      key: "low",
      shortTitle: "≤89/59",
      title: "89/59 mmHg or lower",
      label: "LOW BLOOD PRESSURE",
      shortLabel: "LOW",
      bar: "bg-amber-400",
      badgeBg: isDarkMode
        ? "bg-amber-500/30 text-amber-200"
        : "bg-amber-400 text-white",
      match: (s: number, d: number) => s <= 89 || d <= 59,
    },
    {
      key: "normal",
      shortTitle: "90/60 – 139/89",
      title: "Between 90/60 mmHg and 139/89 mmHg",
      label: "NORMAL BLOOD PRESSURE",
      shortLabel: "NORMAL",
      bar: "bg-emerald-500",
      badgeBg: isDarkMode
        ? "bg-emerald-500/30 text-emerald-200"
        : "bg-emerald-500 text-white",
      match: (s: number, d: number) =>
        s >= 90 && s <= 139 && d >= 60 && d <= 89,
    },
    {
      key: "high",
      shortTitle: "140/90 – 179/119",
      title: "Between 140/90 mmHg and 179/119 mmHg",
      label: "HIGH BLOOD PRESSURE",
      shortLabel: "HIGH",
      bar: "bg-orange-500",
      badgeBg: isDarkMode
        ? "bg-orange-500/30 text-orange-200"
        : "bg-orange-500 text-white",
      match: (s: number, d: number) =>
        (s >= 140 && s <= 179) || (d >= 90 && d <= 119),
    },
    {
      key: "very-high",
      shortTitle: "≥180/120",
      title: "180/120 mmHg or higher",
      label: "VERY HIGH BLOOD PRESSURE",
      shortLabel: "VERY HIGH",
      bar: "bg-rose-600",
      badgeBg: isDarkMode
        ? "bg-rose-500/30 text-rose-200"
        : "bg-rose-600 text-white",
      match: (s: number, d: number) => s >= 180 || d >= 120,
    },
  ];

  const activeKey = avg
    ? bands.find((b) => b.match(avg.systolic, avg.diastolic))?.key
    : null;

  return (
    <div
      className={`mb-4 rounded-xl border p-3 ${
        isDarkMode
          ? "border-gray-700 bg-gray-900/40"
          : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div
          className={`text-xs font-semibold ${
            isDarkMode ? "text-gray-200" : "text-gray-800"
          }`}
        >
          Blood Pressure Readings
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`flex items-center gap-1 text-xs font-medium hover:underline ${
            isDarkMode ? "text-blue-300" : "text-blue-600"
          }`}
        >
          {expanded ? "Collapse" : "Expand"}
          {expanded ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )}
        </button>
      </div>

      {expanded ? (
        <div className="space-y-2">
          {bands.map((b) => {
            const isActive = activeKey === b.key;
            return (
              <div
                key={b.key}
                className={`flex items-stretch gap-2 rounded-md border ${
                  isActive
                    ? isDarkMode
                      ? "border-blue-500 bg-blue-900/20"
                      : "border-blue-500 bg-blue-50"
                    : isDarkMode
                      ? "border-gray-700 bg-gray-800"
                      : "border-gray-200 bg-white"
                }`}
              >
                <div className={`w-1 rounded-l-md ${b.bar}`} />
                <div className="flex-1 py-1.5 pr-2">
                  <div
                    className={`text-xs font-medium ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    {b.title}
                  </div>
                  <span
                    className={`mt-1 inline-block rounded-sm px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${b.badgeBg}`}
                  >
                    {b.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1.5">
          {bands.map((b) => {
            const isActive = activeKey === b.key;
            return (
              <div
                key={b.key}
                title={`${b.title} – ${b.label}`}
                className={`rounded-md border overflow-hidden ${
                  isActive
                    ? isDarkMode
                      ? "border-blue-500 ring-1 ring-blue-500"
                      : "border-blue-500 ring-1 ring-blue-500"
                    : isDarkMode
                      ? "border-gray-700"
                      : "border-gray-200"
                }`}
              >
                <div className={`h-1.5 ${b.bar}`} />
                <div
                  className={`px-1.5 py-1 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <div
                    className={`text-[10px] font-medium leading-tight ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    {b.shortTitle}
                  </div>
                  <span
                    className={`mt-0.5 inline-block rounded-sm px-1 py-px text-[9px] font-semibold tracking-wide ${b.badgeBg}`}
                  >
                    {b.shortLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {avg && activeKey && (
        <div
          className={`mt-2 text-xs ${
            isDarkMode ? "text-gray-300" : "text-gray-600"
          }`}
        >
          Current average ({avg.systolic}/{avg.diastolic} mmHg) falls in the
          highlighted band.
        </div>
      )}
    </div>
  );
}

export function NHSHypertensionInitialForm({
  isDarkMode = false,
  onSave,
  onComplete,
  initialData,
  nhsHypertensionInitialFormLoading = false,
  consultationStatus,
}: NHSHypertensionInitialFormProps) {
  const [formData, setFormData] = useState<NHSHypertensionInitialFormData>({
    ...defaultData,
    ...initialData,
  });
  const [clinicalRefsOpen, setClinicalRefsOpen] = useState(false);

  const stage = getStage("nhs-htn-initial");
  const clinicalLinkRefs = stage.clinicalLinkRefs || [];
  const headerLinks = clinicalLinkRefs.map((ref) => links[ref]).filter(Boolean);

  const handleChange = <K extends keyof NHSHypertensionInitialFormData>(
    field: K,
    value: NHSHypertensionInitialFormData[K],
  ) => setFormData((prev) => ({ ...prev, [field]: value }));

  const styles = buildHypertensionFormStyles(isDarkMode);
  const {
    card,
    sectionBadge,
    sectionTitle,
    labelCls,
    inputCls,
    selectCls,
    selectContentCls,
    radioCls,
    radioLabelCls,
    helperText,
  } = styles;

  const avg = calcAvgBp(formData);
  const meetsEligibility =
    formData.ageEligible === "yes" && formData.alreadyDiagnosed === "no";

  // Auto-derive the consultation outcome from the average clinic BP so
  // clinicians no longer have to pick it manually.
  const derivedOutcome: typeof formData.outcome = (() => {
    if (!avg) return "";
    const { systolic: s, diastolic: d } = avg;
    if (s >= 180 || d >= 120) return "refer-urgent";
    if ((s >= 140 && s <= 179) || (d >= 90 && d <= 119)) return "offer-abpm";
    if (s <= 89 || d <= 59) return "refer-gp";
    return "no-action-normal";
  })();

  useEffect(() => {
    if (derivedOutcome && derivedOutcome !== formData.outcome) {
      handleChange("outcome", derivedOutcome as any);
    }
  }, [derivedOutcome]);

  const canSubmit =
    formData.patientConsent === "yes" &&
    formData.consultationDate !== "" &&
    formData.consultationTime !== "" &&
    formData.consultationMethod !== "" &&
    meetsEligibility &&
    derivedOutcome !== "";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSave) onSave(formData);
    if (onComplete) onComplete(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Service header */}
      <div
        className={`z-10 rounded-2xl p-4 shadow-lg ${
          isDarkMode
            ? "bg-rose-900/20 border border-rose-800"
            : "bg-rose-50 border border-rose-200"
        }`}
        style={{
          backgroundImage: isDarkMode
            ? "linear-gradient(180deg, rgba(159,18,57,0.3) 0%, rgba(159,18,57,0.2) 100%)"
            : "linear-gradient(180deg, rgba(254,240,242,1) 0%, rgba(254,240,242,1) 80%, rgba(254,240,242,0.98) 100%)",
        }}
      >
        <div className="flex items-start gap-3">
          <HeartPulse
            className={`w-5 h-5 mt-0.5 ${
              isDarkMode ? "text-rose-300" : "text-rose-600"
            }`}
          />
          <div className="flex-1">
            <div
              className={`text-sm font-semibold ${
                isDarkMode ? "text-rose-200" : "text-rose-900"
              }`}
            >
              {HYPERTENSION_SERVICE_NAME} – Stage 1: Initial Consultation
            </div>
            <p className={`mt-1 text-xs ${helperText}`}>
              Confirm eligibility, take three clinic blood pressure readings and
              decide whether to offer 24-hour ambulatory blood pressure
              monitoring (ABPM).
            </p>
            <div className={`mt-3 flex flex-wrap gap-1.5 text-[11px]`}>
              {HYPERTENSION_STAGES.map((s) => (
                <span
                  key={s.id}
                  className={`px-2 py-0.5 rounded-full border ${
                    s.number === 1
                      ? isDarkMode
                        ? "bg-rose-800 border-rose-700 text-rose-100"
                        : "bg-rose-600 border-rose-600 text-white"
                      : isDarkMode
                        ? "border-gray-700 text-gray-400"
                        : "border-gray-300 text-gray-500"
                  }`}
                >
                  Stage {s.number}: {s.shortLabel}
                </span>
              ))}
            </div>
            {headerLinks.length > 0 && (
              <ClinicalReferencesCollapsible
                links={headerLinks}
                isDarkMode={isDarkMode}
                isOpen={clinicalRefsOpen}
                onOpenChange={setClinicalRefsOpen}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Section 1: Consent ───────────────────────────────────────────── */}
      <div className={card}>
        <div className="flex items-center gap-3 mb-4">
          <div className={sectionBadge}>1</div>
          <h3 className={sectionTitle}>Patient Consent</h3>
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>
            Does the patient consent to this consultation and to the recording
            of their blood pressure readings?
          </Label>
          <RadioGroup
            value={formData.patientConsent}
            onValueChange={(v) => handleChange("patientConsent", v as any)}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="yes"
                id="htn-i-consent-yes"
                className={radioCls}
              />
              <label htmlFor="htn-i-consent-yes" className={radioLabelCls}>
                Yes
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="no"
                id="htn-i-consent-no"
                className={radioCls}
              />
              <label htmlFor="htn-i-consent-no" className={radioLabelCls}>
                No
              </label>
            </div>
          </RadioGroup>
        </div>
      </div>

      {formData.patientConsent === "yes" && (
        <>
          {/* ── Section 2: Consultation Details ────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>2</div>
              <h3 className={sectionTitle}>Consultation Details</h3>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="htn-i-date" className={labelCls}>
                    Date
                  </Label>
                  <div className="relative">
                    <Input
                      id="htn-i-date"
                      type="date"
                      value={formData.consultationDate}
                      onChange={(e) =>
                        handleChange("consultationDate", e.target.value)
                      }
                      className={inputCls}
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="htn-i-time" className={labelCls}>
                    Time
                  </Label>
                  <Input
                    id="htn-i-time"
                    type="time"
                    value={formData.consultationTime}
                    onChange={(e) =>
                      handleChange("consultationTime", e.target.value)
                    }
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Method of Consultation</Label>
                <Select
                  value={formData.consultationMethod}
                  onValueChange={(v) =>
                    handleChange("consultationMethod", v as any)
                  }
                >
                  <SelectTrigger className={selectCls}>
                    <SelectValue placeholder="Select consultation method" />
                  </SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="face-to-face">Face to Face</SelectItem>
                    <SelectItem value="telephone">Telephone</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="htn-i-method" className={labelCls}>
                  How was the patient identified for service?
                </Label>
                <Select
                  value={formData.methodOfEntry}
                  onValueChange={(v) => handleChange("methodOfEntry", v as any)}
                >
                  <SelectTrigger id="htn-i-method" className={selectCls}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="pharmacy-identified">
                      Patient identified in the pharmacy
                    </SelectItem>
                    <SelectItem value="self-referral">
                      Patient self referral
                    </SelectItem>
                    <SelectItem value="gp-practice">
                      Referred by GP practice
                    </SelectItem>
                    <SelectItem value="covid-vaccination-centre">
                      Referred by COVID vaccination centre
                    </SelectItem>
                    <SelectItem value="optician">
                      Referred by optician
                    </SelectItem>
                    <SelectItem value="dentist">Referred by dentist</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Referrer details — only when an external referrer is chosen */}
              {formData.methodOfEntry !== "" &&
                formData.methodOfEntry !== "pharmacy-identified" &&
                formData.methodOfEntry !== "self-referral" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="htn-i-ref-ref" className={labelCls}>
                        Referrer case reference{" "}
                        <span className={helperText}>(Optional)</span>
                      </Label>
                      <Input
                        id="htn-i-ref-ref"
                        type="text"
                        placeholder="Referrer case reference"
                        value={formData.referrerCaseReference}
                        onChange={(e) =>
                          handleChange("referrerCaseReference", e.target.value)
                        }
                        className={inputCls}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className={labelCls}>Referred for</Label>
                      <RadioGroup
                        value={formData.referredFor}
                        onValueChange={(v) =>
                          handleChange("referredFor", v as any)
                        }
                        className="grid grid-cols-2 gap-3"
                      >
                        {[
                          {
                            value: "clinic-bp-check",
                            label: "Clinic BP check",
                          },
                          { value: "abpm", label: "ABPM" },
                        ].map((opt) => {
                          const selected = formData.referredFor === opt.value;
                          return (
                            <label
                              key={opt.value}
                              htmlFor={`htn-i-rf-${opt.value}`}
                              className={`flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${
                                selected
                                  ? isDarkMode
                                    ? "border-blue-500 bg-blue-900/20"
                                    : "border-blue-500 bg-blue-50"
                                  : isDarkMode
                                    ? "border-gray-600 bg-gray-700"
                                    : "border-gray-300 bg-white"
                              }`}
                            >
                              <RadioGroupItem
                                value={opt.value}
                                id={`htn-i-rf-${opt.value}`}
                                className={radioCls}
                              />
                              <span className={radioLabelCls}>{opt.label}</span>
                            </label>
                          );
                        })}
                      </RadioGroup>
                    </div>
                  </>
                )}
            </div>
          </div>

          {/* ── Section 3: Eligibility ─────────────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>3</div>
              <h3 className={sectionTitle}>Eligibility</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className={labelCls}>
                  Is the patient aged 40 or over?
                </Label>
                <RadioGroup
                  value={formData.ageEligible}
                  onValueChange={(v) => handleChange("ageEligible", v as any)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="age-yes"
                      className={radioCls}
                    />
                    <label htmlFor="age-yes" className={radioLabelCls}>
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="age-no"
                      className={radioCls}
                    />
                    <label htmlFor="age-no" className={radioLabelCls}>
                      No
                    </label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label className={labelCls}>
                  Already diagnosed with hypertension or currently being
                  investigated for it?
                </Label>
                <RadioGroup
                  value={formData.alreadyDiagnosed}
                  onValueChange={(v) =>
                    handleChange("alreadyDiagnosed", v as any)
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="diag-yes"
                      className={radioCls}
                    />
                    <label htmlFor="diag-yes" className={radioLabelCls}>
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="diag-no"
                      className={radioCls}
                    />
                    <label htmlFor="diag-no" className={radioLabelCls}>
                      No
                    </label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label className={labelCls}>Pregnant (where applicable)?</Label>
                <RadioGroup
                  value={formData.pregnant}
                  onValueChange={(v) => handleChange("pregnant", v as any)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yes"
                      id="preg-yes"
                      className={radioCls}
                    />
                    <label htmlFor="preg-yes" className={radioLabelCls}>
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="no"
                      id="preg-no"
                      className={radioCls}
                    />
                    <label htmlFor="preg-no" className={radioLabelCls}>
                      No
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="n-a"
                      id="preg-na"
                      className={radioCls}
                    />
                    <label htmlFor="preg-na" className={radioLabelCls}>
                      N/A
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {!meetsEligibility &&
                formData.ageEligible !== "" &&
                formData.alreadyDiagnosed !== "" && (
                  <Alert
                    className={
                      isDarkMode
                        ? "bg-amber-900/20 border-amber-700 text-amber-200"
                        : "bg-amber-50 border-amber-300 text-amber-900"
                    }
                  >
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      Patient is not eligible for the case-finding service.
                      Refer to GP for management or signpost as appropriate.
                    </AlertDescription>
                  </Alert>
                )}
            </div>
          </div>

          {meetsEligibility && (
            <>
              {/* ── Section 4: Clinic BP Readings ───────────────────────── */}
              <div className={card}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={sectionBadge}>4</div>
                  <h3 className={sectionTitle}>Clinic Blood Pressure</h3>
                </div>
                <p className={`mb-4 ${helperText}`}>
                  Take three seated readings, one minute apart. The first
                  reading is discarded; the average of readings 2 and 3 is used
                  to inform the decision.
                </p>

                {/* BP Reference Chart */}
                <BpReferenceChart isDarkMode={isDarkMode} avg={avg} />

                <div className="space-y-3">
                  {[1, 2, 3].map((i) => {
                    const sysKey =
                      `bp${i}Systolic` as keyof NHSHypertensionInitialFormData;
                    const diaKey =
                      `bp${i}Diastolic` as keyof NHSHypertensionInitialFormData;
                    const armKey =
                      `bp${i}Arm` as keyof NHSHypertensionInitialFormData;
                    return (
                      <div
                        key={i}
                        className="grid grid-cols-[110px_1fr_1fr_120px] gap-3 items-end"
                      >
                        <Label className={labelCls}>
                          Reading {i}
                          {i === 1 ? " (discarded)" : ""}
                        </Label>
                        <div className="space-y-1">
                          <div className={helperText}>Systolic (mmHg)</div>
                          <Input
                            type="number"
                            inputMode="numeric"
                            placeholder="e.g. 142"
                            value={formData[sysKey] as string}
                            onChange={(e) =>
                              handleChange(sysKey, e.target.value as any)
                            }
                            className={inputCls}
                          />
                        </div>
                        <div className="space-y-1">
                          <div className={helperText}>Diastolic (mmHg)</div>
                          <Input
                            type="number"
                            inputMode="numeric"
                            placeholder="e.g. 92"
                            value={formData[diaKey] as string}
                            onChange={(e) =>
                              handleChange(diaKey, e.target.value as any)
                            }
                            className={inputCls}
                          />
                        </div>
                        <div className="space-y-1">
                          <div className={helperText}>Arm</div>
                          <Select
                            value={formData[armKey] as string}
                            onValueChange={(v) =>
                              handleChange(armKey, v as any)
                            }
                          >
                            <SelectTrigger className={selectCls}>
                              <SelectValue placeholder="Arm" />
                            </SelectTrigger>
                            <SelectContent className={selectContentCls}>
                              <SelectItem value="left">Left</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4 items-end">
                  <div className="space-y-2">
                    <Label className={labelCls}>Pulse (bpm)</Label>
                    <Input
                      type="number"
                      inputMode="numeric"
                      value={formData.pulseRate}
                      onChange={(e) =>
                        handleChange("pulseRate", e.target.value)
                      }
                      className={inputCls}
                    />
                  </div>
                  <label
                    htmlFor="htn-i-irregular-pulse"
                    className={`flex items-center gap-2 rounded-md border px-3 h-10 cursor-pointer transition-colors ${
                      formData.irregularPulse
                        ? isDarkMode
                          ? "border-rose-500 bg-rose-900/20"
                          : "border-rose-500 bg-rose-50"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700"
                          : "border-gray-300 bg-white"
                    }`}
                  >
                    <Checkbox
                      id="htn-i-irregular-pulse"
                      checked={formData.irregularPulse}
                      onCheckedChange={(v) =>
                        handleChange("irregularPulse", Boolean(v))
                      }
                    />
                    <span className={radioLabelCls}>
                      Irregular pulse detected
                    </span>
                  </label>
                </div>

                {formData.irregularPulse && (
                  <Alert
                    className={`mt-3 ${
                      isDarkMode
                        ? "bg-amber-900/20 border-amber-700 text-amber-100"
                        : "bg-amber-50 border-amber-300 text-amber-900"
                    }`}
                  >
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      Irregular pulse may indicate atrial fibrillation. ABPM may
                      be unreliable – consider manual readings and referral to
                      GP for further investigation.
                    </AlertDescription>
                  </Alert>
                )}

                {avg &&
                  (() => {
                    const s = avg.systolic;
                    const d = avg.diastolic;
                    let category: {
                      label: string;
                      tone: "low" | "normal" | "high" | "very-high";
                      message: string;
                    };
                    if (s >= 180 || d >= 120) {
                      category = {
                        label: "Very High Blood Pressure",
                        tone: "very-high",
                        message:
                          "Urgent same-day referral indicated (clinic BP ≥180/120 mmHg).",
                      };
                    } else if (
                      (s >= 140 && s <= 179) ||
                      (d >= 90 && d <= 119)
                    ) {
                      category = {
                        label: "High Blood Pressure",
                        tone: "high",
                        message:
                          "Above NICE threshold – offer ABPM to confirm diagnosis.",
                      };
                    } else if (s <= 89 || d <= 59) {
                      category = {
                        label: "Low Blood Pressure",
                        tone: "low",
                        message:
                          "Below normal range – consider symptoms and refer to GP if appropriate.",
                      };
                    } else {
                      category = {
                        label: "Normal Blood Pressure",
                        tone: "normal",
                        message:
                          "Within normal range – no further hypertension action required.",
                      };
                    }

                    const toneStyles: Record<
                      typeof category.tone,
                      { bg: string; chip: string; text: string }
                    > = {
                      low: {
                        bg: isDarkMode
                          ? "bg-amber-900/20 border border-amber-700"
                          : "bg-amber-50 border border-amber-300",
                        chip: isDarkMode
                          ? "bg-amber-500/30 text-amber-200"
                          : "bg-amber-400 text-white",
                        text: isDarkMode ? "text-amber-100" : "text-amber-900",
                      },
                      normal: {
                        bg: isDarkMode
                          ? "bg-emerald-900/20 border border-emerald-700"
                          : "bg-emerald-50 border border-emerald-300",
                        chip: isDarkMode
                          ? "bg-emerald-500/30 text-emerald-200"
                          : "bg-emerald-500 text-white",
                        text: isDarkMode
                          ? "text-emerald-100"
                          : "text-emerald-900",
                      },
                      high: {
                        bg: isDarkMode
                          ? "bg-orange-900/20 border border-orange-700"
                          : "bg-orange-50 border border-orange-300",
                        chip: isDarkMode
                          ? "bg-orange-500/30 text-orange-200"
                          : "bg-orange-500 text-white",
                        text: isDarkMode
                          ? "text-orange-100"
                          : "text-orange-900",
                      },
                      "very-high": {
                        bg: isDarkMode
                          ? "bg-rose-900/20 border border-rose-700"
                          : "bg-rose-50 border border-rose-300",
                        chip: isDarkMode
                          ? "bg-rose-500/30 text-rose-200"
                          : "bg-rose-600 text-white",
                        text: isDarkMode ? "text-rose-100" : "text-rose-900",
                      },
                    };
                    const t = toneStyles[category.tone];

                    return (
                      <div
                        className={`mt-4 rounded-xl p-3 text-sm ${t.bg} ${t.text}`}
                      >
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            Average of readings 2 &amp; 3:{" "}
                            <span className="font-semibold">
                              {s}/{d} mmHg
                            </span>
                          </div>
                          <span
                            className={`rounded-md px-2 py-0.5 text-xs font-semibold tracking-wide ${t.chip}`}
                          >
                            {category.label}
                          </span>
                        </div>
                        <div className="mt-1 text-xs">{category.message}</div>
                      </div>
                    );
                  })()}
              </div>

              {/* ── Section 5: Symptoms (only when BP is low) ───────────── */
              /* Shown when the average BP falls in the low band
                 (systolic ≤ 89 or diastolic ≤ 59 mmHg). */}
              {(() => {
                const isLowBp =
                  avg !== null && (avg.systolic <= 89 || avg.diastolic <= 59);
                if (!isLowBp) return null;

                const toggle = <T extends string>(list: T[], value: T): T[] =>
                  list.includes(value)
                    ? list.filter((v) => v !== value)
                    : [...list, value];

                const showFaintingDetail =
                  formData.lowBpSymptomCategory === "fainting";
                const showDnfDetail =
                  formData.lowBpSymptomCategory === "dizziness-nausea-fatigue";

                return (
                  <div className={card}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={sectionBadge}>5</div>
                      <h3 className={sectionTitle}>Symptoms</h3>
                    </div>

                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label className={labelCls}>
                          Does the patient experience any symptoms?
                        </Label>
                        <RadioGroup
                          value={formData.lowBpSymptomCategory}
                          onValueChange={(v) => {
                            handleChange("lowBpSymptomCategory", v as any);
                            // Clear detail lists for the non-selected category
                            if (v !== "fainting")
                              handleChange("lowBpFaintingSymptoms", []);
                            if (v !== "dizziness-nausea-fatigue")
                              handleChange("lowBpDnfSymptoms", []);
                          }}
                          className="space-y-2"
                        >
                          {[
                            {
                              value: "fainting",
                              label:
                                "Fainting / falling, or regularly feeling like they will faint",
                            },
                            {
                              value: "dizziness-nausea-fatigue",
                              label: "Dizziness, nausea or fatigue",
                            },
                            {
                              value: "none",
                              label: "None of the above",
                            },
                          ].map((opt) => {
                            const selected =
                              formData.lowBpSymptomCategory === opt.value;
                            return (
                              <label
                                key={opt.value}
                                htmlFor={`htn-i-sym-${opt.value}`}
                                className={`flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${
                                  selected
                                    ? isDarkMode
                                      ? "border-blue-500 bg-blue-900/20"
                                      : "border-blue-500 bg-blue-50"
                                    : isDarkMode
                                      ? "border-gray-600 bg-gray-700"
                                      : "border-gray-300 bg-white"
                                }`}
                              >
                                <RadioGroupItem
                                  value={opt.value}
                                  id={`htn-i-sym-${opt.value}`}
                                  className={radioCls}
                                />
                                <span className={radioLabelCls}>
                                  {opt.label}
                                </span>
                              </label>
                            );
                          })}
                        </RadioGroup>
                      </div>

                      {(showFaintingDetail || showDnfDetail) && (
                        <div className="space-y-3">
                          <Label className={labelCls}>
                            Which symptoms are experienced?
                          </Label>
                          <div className="space-y-2">
                            {(showFaintingDetail
                              ? [
                                  {
                                    value: "regular-fainting",
                                    label: "Regular fainting",
                                  },
                                  { value: "falls", label: "Falls" },
                                  {
                                    value: "feel-may-faint-daily",
                                    label:
                                      "Feel like they may faint on a daily / near daily basis",
                                  },
                                ]
                              : [
                                  { value: "dizziness", label: "Dizziness" },
                                  { value: "nausea", label: "Nausea" },
                                  { value: "fatigue", label: "Fatigue" },
                                ]
                            ).map((opt) => {
                              const list = showFaintingDetail
                                ? formData.lowBpFaintingSymptoms
                                : formData.lowBpDnfSymptoms;
                              const checked = (list as string[]).includes(
                                opt.value,
                              );
                              return (
                                <label
                                  key={opt.value}
                                  htmlFor={`htn-i-symd-${opt.value}`}
                                  className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                                    checked
                                      ? isDarkMode
                                        ? "border-blue-500 bg-blue-900/20"
                                        : "border-blue-500 bg-blue-50"
                                      : isDarkMode
                                        ? "border-gray-600 bg-gray-700"
                                        : "border-gray-300 bg-white"
                                  }`}
                                >
                                  <Checkbox
                                    id={`htn-i-symd-${opt.value}`}
                                    checked={checked}
                                    onCheckedChange={() => {
                                      if (showFaintingDetail) {
                                        handleChange(
                                          "lowBpFaintingSymptoms",
                                          toggle(
                                            formData.lowBpFaintingSymptoms,
                                            opt.value as any,
                                          ),
                                        );
                                      } else {
                                        handleChange(
                                          "lowBpDnfSymptoms",
                                          toggle(
                                            formData.lowBpDnfSymptoms,
                                            opt.value as any,
                                          ),
                                        );
                                      }
                                    }}
                                  />
                                  <span className={radioLabelCls}>
                                    {opt.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {showFaintingDetail && (
                        <Alert
                          className={
                            isDarkMode
                              ? "bg-rose-900/20 border-rose-700 text-rose-100"
                              : "bg-rose-50 border-rose-300 text-rose-900"
                          }
                        >
                          <ShieldAlert className="h-4 w-4" />
                          <AlertDescription className="text-sm">
                            <div className="font-semibold">
                              Low blood pressure with symptoms identified
                            </div>
                            <div className="mt-1">
                              Refer patient to their registered GP for a
                              same-day appointment. You should call the general
                              practice during opening hours and while the
                              patient is still in the pharmacy.
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}

                      {showDnfDetail && (
                        <Alert
                          className={
                            isDarkMode
                              ? "bg-amber-900/20 border-amber-700 text-amber-100"
                              : "bg-amber-50 border-amber-300 text-amber-900"
                          }
                        >
                          <ShieldAlert className="h-4 w-4" />
                          <AlertDescription className="text-sm">
                            <div className="font-semibold">
                              Low blood pressure with symptoms identified
                            </div>
                            <div className="mt-1">
                              Refer patient to their registered GP or another
                              healthcare provider within 3 weeks.
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ── Section 6: ABPM Consent ─────────────────────────────── */
              /* Hidden when the average BP is within the normal range
                 (90/60 – 139/89 mmHg) or in the low band
                 (≤89/59 mmHg) – an ABPM is not required. */}
              {(() => {
                const isNormalBp =
                  avg !== null &&
                  avg.systolic >= 90 &&
                  avg.systolic <= 139 &&
                  avg.diastolic >= 60 &&
                  avg.diastolic <= 89;
                const isLowBp =
                  avg !== null && (avg.systolic <= 89 || avg.diastolic <= 59);
                if (!avg) return null;
                if (isNormalBp || isLowBp) return null;
                return (
                  <div className={card}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={sectionBadge}>6</div>
                      <h3 className={sectionTitle}>ABPM Consent</h3>
                    </div>
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label className={labelCls}>
                          Does the patient consent to an ABPM?
                        </Label>
                        <RadioGroup
                          value={formData.abpmConsent}
                          onValueChange={(v) => {
                            handleChange("abpmConsent", v as any);
                            if (v === "no")
                              handleChange("abpmAvailableNow", "");
                          }}
                          className="flex gap-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="yes"
                              id="abpm-consent-yes"
                              className={radioCls}
                            />
                            <label
                              htmlFor="abpm-consent-yes"
                              className={radioLabelCls}
                            >
                              Yes
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="no"
                              id="abpm-consent-no"
                              className={radioCls}
                            />
                            <label
                              htmlFor="abpm-consent-no"
                              className={radioLabelCls}
                            >
                              No
                            </label>
                          </div>
                        </RadioGroup>
                      </div>

                      {formData.abpmConsent === "yes" && (
                        <div className="space-y-2">
                          <Label className={labelCls}>
                            Is an ABPM available to fit now?
                          </Label>
                          <RadioGroup
                            value={formData.abpmAvailableNow}
                            onValueChange={(v) =>
                              handleChange("abpmAvailableNow", v as any)
                            }
                            className="flex gap-4"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="yes"
                                id="abpm-avail-yes"
                                className={radioCls}
                              />
                              <label
                                htmlFor="abpm-avail-yes"
                                className={radioLabelCls}
                              >
                                Yes
                              </label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem
                                value="no"
                                id="abpm-avail-no"
                                className={radioCls}
                              />
                              <label
                                htmlFor="abpm-avail-no"
                                className={radioLabelCls}
                              >
                                No
                              </label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ── Section 7: Healthy Living Advice ────────────────────── */}
              {/* The remaining sections (Healthy Living, Outcome and the
                 Treatment Plan) only become relevant once the clinic blood
                 pressure readings (2 & 3) are recorded, since their content
                 depends on the BP category. */}
              {avg && (
                <>
                  <div className={card}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={sectionBadge}>7</div>
                      <h3 className={sectionTitle}>Healthy Living Advice</h3>
                    </div>
                    <p className={`mb-3 ${helperText}`}>
                      Select the healthy living advice areas you covered with
                      the patient during this consultation.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: "diet-nutrition", label: "Diet & nutrition" },
                        { value: "sodium-salt", label: "Sodium / salt" },
                        { value: "caffeine", label: "Caffeine" },
                        { value: "alcohol", label: "Alcohol" },
                        { value: "smoking", label: "Smoking" },
                        {
                          value: "physical-activity",
                          label: "Physical activity",
                        },
                        {
                          value: "referral-local-healthy-living",
                          label: "Referral to a local Healthy Living service",
                        },
                      ].map((opt) => {
                        const checked = (
                          formData.healthyLivingAdvice as string[]
                        ).includes(opt.value);
                        return (
                          <label
                            key={opt.value}
                            htmlFor={`htn-i-hla-${opt.value}`}
                            className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                              checked
                                ? isDarkMode
                                  ? "border-blue-500 bg-blue-900/20"
                                  : "border-blue-500 bg-blue-50"
                                : isDarkMode
                                  ? "border-gray-600 bg-gray-700"
                                  : "border-gray-300 bg-white"
                            }`}
                          >
                            <Checkbox
                              id={`htn-i-hla-${opt.value}`}
                              checked={checked}
                              onCheckedChange={() => {
                                const current = formData.healthyLivingAdvice;
                                const next = current.includes(opt.value as any)
                                  ? current.filter((v) => v !== opt.value)
                                  : [...current, opt.value as any];
                                handleChange("healthyLivingAdvice", next);
                              }}
                            />
                            <span className={radioLabelCls}>{opt.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label htmlFor="htn-i-hla-notes" className={labelCls}>
                        Additional notes (optional)
                      </Label>
                      <Textarea
                        id="htn-i-hla-notes"
                        rows={2}
                        value={formData.healthyLivingAdviceNotes}
                        onChange={(e) =>
                          handleChange(
                            "healthyLivingAdviceNotes",
                            e.target.value,
                          )
                        }
                        className={inputCls}
                      />
                    </div>
                  </div>

                  {/* ── Section 9: Treatment Plan / Next Stages ─────────────── */}
                  {formData.outcome === "offer-abpm" && (
                    <div className={card}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className={sectionBadge}>9</div>
                        <h3 className={sectionTitle}>
                          Treatment Plan – Schedule Next Stages
                        </h3>
                      </div>
                      <Alert
                        className={
                          isDarkMode
                            ? "bg-blue-900/20 border-blue-700 text-blue-100 mb-4"
                            : "bg-blue-50 border-blue-200 text-blue-900 mb-4"
                        }
                      >
                        <ListOrdered className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                          Completing this stage will create a Treatment Plan
                          with goals and follow-up appointments for the
                          remaining stages (ABPM Fitting and ABPM Follow Up).
                          Stages can only be started in order.
                        </AlertDescription>
                      </Alert>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className={labelCls}>
                            Proposed ABPM fitting date
                          </Label>
                          <Input
                            type="date"
                            value={formData.abpmFittingDate}
                            onChange={(e) =>
                              handleChange("abpmFittingDate", e.target.value)
                            }
                            className={inputCls}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={labelCls}>
                            Proposed ABPM follow-up date
                          </Label>
                          <Input
                            type="date"
                            value={formData.abpmFollowUpDate}
                            onChange={(e) =>
                              handleChange("abpmFollowUpDate", e.target.value)
                            }
                            className={inputCls}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={card}>
                    <div className="space-y-2">
                      <Label htmlFor="htn-i-internal" className={labelCls}>
                        Internal notes
                      </Label>
                      <Textarea
                        id="htn-i-internal"
                        rows={3}
                        value={formData.internalNotes}
                        onChange={(e) =>
                          handleChange("internalNotes", e.target.value)
                        }
                        className={inputCls}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="submit"
          disabled={
            nhsHypertensionInitialFormLoading ||
            consultationStatus === 1 ||
            !canSubmit
          }
        >
          {nhsHypertensionInitialFormLoading
            ? "Please wait..."
            : "Complete Stage 1"}
        </Button>
      </div>
    </form>
  );
}
